import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Cancel, ContentCopy, Delete, Edit, Fullscreen, FullscreenExit, MoreVert, PlayArrow, Refresh, Save, Star, StarBorder } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Menu, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';

import { formatDuration, useApiClient, useCopyToClipboard, useToastService } from '@@common';
import { ConfirmDialog, useNotificationRefetch, useNotifications } from '@@components';
import type { FlowInstanceEventNotification } from '@@components';

import { FlowClient, FlowInstanceRunModel, FlowInstanceStatus } from '../../../api/api';
import { formatTimestampSortable } from '../../../common/formatTimestamp';
import useApiEnvironments from '../../../common/hooks/useApiEnvironments';
import CopyToClipboardButton from '../../../components/Clipboard/CopyToClipboardButton';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import type { ExecutionTraceStep } from '../Hooks/useFlowExecutionTrace';
import usePinnedFlowInstances from '../Hooks/usePinnedFlowInstances';
import useSelectedFlowInstanceUrlState from '../Hooks/useSelectedFlowInstanceUrlState';
import { FlowNode } from '../Models/GraphModel';
import { getFlowInstanceLiveState } from '../Services/getFlowInstanceLiveState';
import ActivityNameDisplay from './ActivityNameDisplay';
import FlowInstanceAttributeItem from './FlowInstanceAttributeItem';
import FlowInstanceDetailRow from './FlowInstanceDetailRow';
import FlowInstanceExecutionRuns from './FlowInstanceExecutionRuns';
import JsonDataDisplay from '../../../common/components/JsonDataDisplay';
import RunFlowModal from './RunFlowModal';

const FLOW_INSTANCE_DETAILS_PANEL_STORAGE_KEY = 'flowInstanceDetailsPanel.width';
const FLOW_PROGRESS_REFRESH_MS = 3000;
const FLOW_INSTANCE_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const FLOW_INSTANCE_DETAILS_PANEL_MIN_WIDTH = 360;
const FLOW_INSTANCE_DETAILS_PANEL_MAX_WIDTH = 1000;
const FLOW_INSTANCE_DETAILS_ACTIVITY_STACK_EXPANDED_STORAGE_KEY = 'flowInstanceDetailsPanel.currentActivityStack.expanded';
const FLOW_INSTANCE_DETAILS_INPUT_EXPANDED_STORAGE_KEY = 'flowInstanceDetailsPanel.input.expanded';
const FLOW_INSTANCE_DETAILS_OUTPUT_EXPANDED_STORAGE_KEY = 'flowInstanceDetailsPanel.output.expanded';

interface FlowInstanceDetailsPanelProps {
	instanceId: string;
	onClose: () => void;
	onSelectNode?: (nodeId: string | null) => void;
	onInstanceChanged?: (instanceId: string) => void;
	onInstanceResumed?: () => void;
	onInstanceCanceled?: () => void;
	onExecutionStepsChanged?: (steps: ExecutionTraceStep[]) => void;
	onInstanceStatusChanged?: (status: FlowInstanceStatus | undefined) => void;
	onInstanceCurrentActivityChanged?: (activityId: string | undefined) => void;
	listPanelCollapsed?: boolean;
	top?: number;
	bottom?: number;
	rightOffset?: number;
	nodes: FlowNode[];
}

export default function FlowInstanceDetailsPanel({
	instanceId,
	onClose,
	onSelectNode,
	onInstanceChanged,
	onInstanceResumed,
	onInstanceCanceled,
	onExecutionStepsChanged,
	onInstanceStatusChanged,
	onInstanceCurrentActivityChanged,
	listPanelCollapsed = false,
	top = 0,
	bottom = 0,
	rightOffset = FLOW_INSTANCE_DETAILS_PANEL_DEFAULT_WIDTH,
	nodes,
}: FlowInstanceDetailsPanelProps) {
	const { selectedStepId, selectStep } = useSelectedFlowInstanceUrlState();
	const [panelWidth, setPanelWidth] = useState(FLOW_INSTANCE_DETAILS_PANEL_DEFAULT_WIDTH);
	const [isFullWidth, setIsFullWidth] = useState(false);
	const [actionsMenuAnchor, setActionsMenuAnchor] = useState<HTMLElement | null>(null);
	const client = useApiClient(FlowClient);
	const toastService = useToastService();
	const { data: environments = [] } = useApiEnvironments();
	const { flowInstanceEvents, connectionState } = useNotifications();
	const latestFlowInstanceEvent = flowInstanceEvents.find((event) => event.payload.id === instanceId);
	const [refreshKey, setRefreshKey] = useState(0);
	const [previousStatus, setPreviousStatus] = useState<FlowInstanceStatus | undefined>(undefined);
	const [deletingInstance, setDeletingInstance] = useState(false);
	const [cancelingInstance, setCancelingInstance] = useState(false);
	const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
	const [resumeModalOpen, setResumeModalOpen] = useState(false);
	const [savingActivityStack, setSavingActivityStack] = useState(false);
	const [isEditingActivity, setIsEditingActivity] = useState(false);
	const [editedActivityId, setEditedActivityId] = useState('');
	const [savingActivity, setSavingActivity] = useState(false);
	const [deleteRunConfirmDialog, setDeleteRunConfirmDialog] = useState<string | null>(null);

	// Query to fetch flow instance details
	const {
		data: persistedInstanceData,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useQuery({
		queryKey: ['flow-instance-details', instanceId, refreshKey],
		queryFn: async () => {
			return await client.getInstanceById(instanceId, true);
		},
	});

	const instanceData = useMemo(() => getFlowInstanceLiveState(persistedInstanceData, flowInstanceEvents), [persistedInstanceData, flowInstanceEvents]);
	const { isPinned, togglePin } = usePinnedFlowInstances(instanceData?.definitionId);

	// Query to fetch flow instance runs
	const { data: runsData, refetch: refetchRuns } = useQuery({
		queryKey: ['flow-instance-runs', instanceId, refreshKey],
		queryFn: async () => {
			return await client.getInstanceRuns(instanceId);
		},
		enabled: !!instanceId,
	});
	const selectedStep = useMemo(() => runsData?.flatMap((run) => run.steps ?? []).find((step) => step.id === selectedStepId), [runsData, selectedStepId]);
	useEffect(() => {
		if (selectedStep) onSelectNode?.(selectedStep.activityId);
	}, [selectedStep, onSelectNode]);

	// Create a map of activityId to activity type name from nodes
	const activityIdToTypeMap = useMemo(() => {
		const map = new Map<string, string>();
		nodes.forEach((node) => {
			const data = node.data as any; // ActivityModel extends FlowNodeModel
			if (data?.Id && data?.Type) {
				map.set(data.Id, data.Type);
			}
		});
		return map;
	}, [nodes]);

	const activityIdToDataMap = useMemo(() => {
		const map = new Map<string, any>();
		nodes.forEach((node) => {
			const data = node.data as any; // ActivityModel extends FlowNodeModel
			if (data?.Id) {
				map.set(data.Id, data);
			}
		});
		return map;
	}, [nodes]);

	// Helper function to get activity type from activityId
	const getActivityType = useCallback(
		(activityId: string | undefined) => {
			if (!activityId) return undefined;
			return activityIdToTypeMap.get(activityId);
		},
		[activityIdToTypeMap],
	);

	// Helper function to get activity data from activityId
	const getActivityData = useCallback(
		(activityId: string | undefined) => {
			if (!activityId) return undefined;
			return activityIdToDataMap.get(activityId);
		},
		[activityIdToDataMap],
	);

	const refetchInstance = useCallback(() => {
		refetch();
		refetchRuns();
	}, [refetch, refetchRuns]);

	useNotificationRefetch({
		notification: latestFlowInstanceEvent,
		getKey: useCallback((event: FlowInstanceEventNotification) => `${event.payload.id}:${event.receivedAt}`, []),
		shouldRefetch: useCallback((event: FlowInstanceEventNotification) => event.payload.id === instanceId, [instanceId]),
		refetch: refetchInstance,
	});

	useEffect(() => {
		refetchInstance();
	}, [connectionState, refetchInstance]);

	useEffect(() => {
		if (
			instanceData?.status === FlowInstanceStatus.Completed ||
			instanceData?.status === FlowInstanceStatus.Failed ||
			instanceData?.status === FlowInstanceStatus.Canceled
		) {
			// The completion notification can arrive before the trace transaction commits.
			const timer = setTimeout(refetchInstance, FLOW_PROGRESS_REFRESH_MS);
			return () => clearTimeout(timer);
		}
		const timer = setInterval(refetchInstance, FLOW_PROGRESS_REFRESH_MS);
		return () => clearInterval(timer);
	}, [instanceData?.status, refetchInstance]);

	useEffect(() => {
		const steps = (runsData ?? [])
			.slice()
			.sort((left, right) => new Date(left.createdDate).getTime() - new Date(right.createdDate).getTime())
			.flatMap((run: FlowInstanceRunModel, executionRunIndex) =>
				(run.steps ?? [])
					.map((step) => ({
						...step,
						executionRunIndex,
						runId: run.runId,
					}))
					.sort((left, right) => left.order - right.order || new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime()),
			);
		onExecutionStepsChanged?.(steps);
	}, [runsData, onExecutionStepsChanged]);

	useEffect(() => {
		onInstanceStatusChanged?.(instanceData?.status);
	}, [instanceData?.status, onInstanceStatusChanged]);

	useEffect(() => {
		onInstanceCurrentActivityChanged?.(instanceData?.currentActivityId);
	}, [instanceData?.currentActivityId, onInstanceCurrentActivityChanged]);

	// Track status changes
	useEffect(() => {
		if (instanceData?.status && previousStatus !== undefined && previousStatus !== instanceData.status) {
			// Status has changed
			console.log(`Status changed from ${previousStatus} to ${instanceData.status}`);
		}
		setPreviousStatus(instanceData?.status);
	}, [instanceData?.status, previousStatus]);

	const handleRefresh = () => {
		setRefreshKey((prev) => prev + 1);
		refetch();
		refetchRuns();
	};

	const copyToClipboard = useCopyToClipboard();

	const handleCopyId = () => {
		copyToClipboard(instanceId, (id) => String(id));
	};

	const handleDeleteClick = () => {
		setDeleteConfirmDialog(true);
	};

	const handleConfirmDelete = async () => {
		setDeleteConfirmDialog(false);
		setDeletingInstance(true);

		try {
			await client.deleteInstance(instanceId);
			toastService.success('Flow instance deleted successfully.');
			// Notify parent component about the deletion
			onInstanceChanged?.(instanceId);
			// Close the details panel
			onClose();
		} catch (error) {
			console.error('Error deleting flow instance:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete flow instance: ${errorMessage}`);
		} finally {
			setDeletingInstance(false);
		}
	};

	const handleCancelDelete = () => {
		setDeleteConfirmDialog(false);
	};

	const handleResumeClick = () => {
		setResumeModalOpen(true);
	};

	const handleCancelInstance = async () => {
		setCancelingInstance(true);
		try {
			await client.cancelInstance(instanceId);
			toastService.success('Flow cancellation requested. Completed changes are not rolled back.');
			refetchInstance();
			onInstanceCanceled?.();
		} catch (error) {
			toastService.error(error instanceof Error ? error.message : 'Could not cancel the flow instance.');
		} finally {
			setCancelingInstance(false);
		}
	};

	const handleResumeSuccess = (_newInstanceId: string) => {
		onInstanceStatusChanged?.(FlowInstanceStatus.Running);
		// Refresh the current instance details
		handleRefresh();
		// Notify parent to refresh instances list without remounting the panel
		onInstanceResumed?.();
	};

	const handleSaveActivityStack = async (newStack: any) => {
		setSavingActivityStack(true);
		try {
			await client.updateInstance(instanceId, {
				currentActivityStack: newStack,
			});
			toastService.success('Activity stack updated successfully');
			handleRefresh();
		} catch (error) {
			toastService.error('Failed to update activity stack');
			throw error;
		} finally {
			setSavingActivityStack(false);
		}
	};

	const handleEditActivity = () => {
		setEditedActivityId(instanceData?.currentActivityId || '');
		setIsEditingActivity(true);
	};

	const handleCancelEditActivity = () => {
		setIsEditingActivity(false);
		setEditedActivityId('');
	};

	const handleSaveActivity = async () => {
		setSavingActivity(true);
		try {
			await client.updateInstance(instanceId, {
				currentActivityId: editedActivityId || undefined,
			});
			toastService.success('Current activity updated successfully');
			setIsEditingActivity(false);
			handleRefresh();
		} catch (error) {
			toastService.error('Failed to update current activity');
		} finally {
			setSavingActivity(false);
		}
	};

	const handleDeleteRunClick = (runId: string) => {
		setDeleteRunConfirmDialog(runId);
	};

	const handleTogglePin = () => {
		if (!instanceData?.definitionId) {
			return;
		}

		togglePin(instanceId);
		toastService.success(isPinned(instanceId) ? 'Flow instance unpinned.' : 'Flow instance pinned.');
	};

	const handleDeleteFromMenu = () => {
		setActionsMenuAnchor(null);
		handleDeleteClick();
	};

	const handleStepClick = useCallback(
		(_stepIndex: number, activityId?: string, stepId?: string) => {
			if (stepId) selectStep(stepId);
			if (onSelectNode && activityId) {
				onSelectNode(activityId);
			}
		},
		[onSelectNode, selectStep],
	);

	const handleConfirmDeleteRun = async () => {
		if (!deleteRunConfirmDialog) return;

		const runIdToDelete = deleteRunConfirmDialog;
		setDeleteRunConfirmDialog(null);

		try {
			await client.deleteInstanceRun(instanceId, runIdToDelete);
			toastService.success('Flow run deleted successfully.');
			handleRefresh();
		} catch (error) {
			console.error('Error deleting flow run:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to delete flow run: ${errorMessage}`);
		}
	};

	const handleCancelDeleteRun = () => {
		setDeleteRunConfirmDialog(null);
	};

	// Header actions with resume and delete buttons
	const headerActions = (
		<Stack direction="row" spacing={1} alignItems="center">
			{(instanceData?.status === FlowInstanceStatus.Running || instanceData?.status === FlowInstanceStatus.Waiting) && (
				<Tooltip title="Cancel flow instance">
					<span style={{ display: 'inline-flex' }}><IconButton size="small" color="warning" aria-label="Cancel flow instance" disabled={cancelingInstance} onClick={handleCancelInstance}>
						{cancelingInstance ? <CircularProgress size={18} color="inherit" /> : <Cancel fontSize="small" />}
					</IconButton></span>
				</Tooltip>
			)}
			<Tooltip title="Resume flow instance">
				<IconButton size="small" onClick={handleResumeClick} sx={{ color: 'primary.main' }}>
					<PlayArrow />
				</IconButton>
			</Tooltip>
			<Tooltip title={isFullWidth ? 'Restore panel size' : 'Maximize panel'}>
				<IconButton size="small" onClick={() => setIsFullWidth((fullWidth) => !fullWidth)} aria-label={isFullWidth ? 'Restore panel size' : 'Maximize panel'}>
					{isFullWidth ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
				</IconButton>
			</Tooltip>
			<Tooltip title="More actions">
				<IconButton size="small" onClick={(event) => setActionsMenuAnchor(event.currentTarget)} aria-label="More actions">
					<MoreVert />
				</IconButton>
			</Tooltip>
			<Menu anchorEl={actionsMenuAnchor} open={Boolean(actionsMenuAnchor)} onClose={() => setActionsMenuAnchor(null)} sx={{ zIndex: 1400 }}>
				<MenuItem onClick={() => { setActionsMenuAnchor(null); handleRefresh(); }} disabled={isFetching}>
					<Refresh fontSize="small" sx={{ mr: 1.5 }} />
					Refresh
				</MenuItem>
				<MenuItem onClick={() => { setActionsMenuAnchor(null); handleTogglePin(); }} disabled={!instanceData?.definitionId}>
					{isPinned(instanceId) ? <StarBorder fontSize="small" sx={{ mr: 1.5 }} /> : <Star fontSize="small" sx={{ mr: 1.5 }} />}
					{isPinned(instanceId) ? 'Unpin flow instance' : 'Pin flow instance'}
				</MenuItem>
				<MenuItem onClick={handleDeleteFromMenu} disabled={deletingInstance} sx={{ color: 'error.main' }}>
					<Delete fontSize="small" sx={{ mr: 1.5 }} />
					Delete flow instance
				</MenuItem>
			</Menu>
		</Stack>
	);

	const getStatusColor = (status?: FlowInstanceStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
		switch (status) {
			case FlowInstanceStatus.Completed:
				return 'success';
			case FlowInstanceStatus.Running:
				return 'info';
			case FlowInstanceStatus.Failed:
				return 'error';
			case FlowInstanceStatus.Waiting:
				return 'warning';
			default:
				return 'default';
		}
	};

	const getStatusLabel = (status?: FlowInstanceStatus) => {
		switch (status) {
			case FlowInstanceStatus.Completed:
				return 'Completed';
			case FlowInstanceStatus.Running:
				return 'Running';
			case FlowInstanceStatus.Failed:
				return 'Failed';
			case FlowInstanceStatus.Waiting:
				return 'Waiting';
			default:
				return 'Unknown';
		}
	};

	const environmentName = useMemo(() => {
		if (!instanceData?.environmentId) return undefined;
		return environments.find((environment) => environment.id === instanceData.environmentId)?.name;
	}, [environments, instanceData?.environmentId]);

	const hasInput = instanceData?.input !== undefined && instanceData.input !== null;
	const hasOutput = instanceData?.output !== undefined && instanceData.output !== null;
	const attributeEntries = useMemo(
		() => Object.entries(instanceData?.attributes ?? {}).sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey)),
		[instanceData?.attributes],
	);

	// Subtitle component with ID and copy button
	const subtitle = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="body2" color="textSecondary" sx={{ flexShrink: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
				{instanceId}
			</Typography>
			<IconButton size="small" onClick={handleCopyId} title="Copy ID to clipboard" sx={{ p: 0.5 }}>
				<ContentCopy sx={{ fontSize: 14 }} />
			</IconButton>
		</Box>
	);

	return (
		<SidePanelDetail
			title="Instance Details"
			subtitle={subtitle}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error as Error | null}
			errorMessage="Failed to load instance details"
			onClose={onClose}
			headerActions={headerActions}
			position="stacked"
			top={top}
			bottom={bottom}
			rightOffset={rightOffset}
			width={panelWidth}
			zIndex={1310}
			listPanelCollapsed={listPanelCollapsed}
			isFullWidth={isFullWidth}
			resizeStorageKey={FLOW_INSTANCE_DETAILS_PANEL_STORAGE_KEY}
			defaultWidth={FLOW_INSTANCE_DETAILS_PANEL_DEFAULT_WIDTH}
			minWidth={FLOW_INSTANCE_DETAILS_PANEL_MIN_WIDTH}
			maxWidth={FLOW_INSTANCE_DETAILS_PANEL_MAX_WIDTH}
			setWidth={setPanelWidth}
		>
			{instanceData && (
				<>
					<Box sx={{ mb: 2 }}>
						<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
							<Typography variant="body2" color="textSecondary" sx={{ minWidth: '120px' }}>
								Status
							</Typography>
							<Box sx={{ display: 'flex', flex: 1 }}>
								<Chip
									label={getStatusLabel(instanceData.status)}
									color={getStatusColor(instanceData.status)}
									size="small"
									sx={{
										animation: instanceData.status === FlowInstanceStatus.Running ? 'pulse 2s ease-in-out infinite' : 'none',
										'@keyframes pulse': {
											'0%': { opacity: 0.8 },
											'50%': { opacity: 1 },
											'100%': { opacity: 0.8 },
										},
									}}
								/>
							</Box>
						</Stack>
						<FlowInstanceDetailRow label="Environment" value={environmentName || 'N/A'} />
						<FlowInstanceDetailRow
							label="Definition ID"
							value={instanceData.definitionId}
							copyValue={instanceData.definitionId}
							copyTitle="Copy Definition ID"
							monospace
						/>
						{instanceData.currentActivityId && (
							<FlowInstanceDetailRow
								label="Current Activity ID"
								value={instanceData.currentActivityId}
								copyValue={instanceData.currentActivityId}
								copyTitle="Copy Current Activity ID"
								monospace
							/>
						)}
						<FlowInstanceDetailRow label="Created" value={formatTimestampSortable(instanceData.createdDate)} />
						<FlowInstanceDetailRow label="Updated" value={formatTimestampSortable(instanceData.updatedDate)} />
						{instanceData.timeToLive && <FlowInstanceDetailRow label="Time To Live" value={instanceData.timeToLive} />}
					</Box>

					{attributeEntries.length > 0 && (
						<>
							<Divider sx={{ my: 2 }} />
							<Box sx={{ mb: 2 }}>
								<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
									Attributes
								</Typography>
								<Stack spacing={0.75}>
									{attributeEntries.map(([key, value]) => (
										<FlowInstanceAttributeItem key={key} attributeKey={key} value={value} />
									))}
								</Stack>
							</Box>
						</>
					)}

					{instanceData.errorMessage && (
						<>
							<Divider sx={{ my: 2 }} />
							<Box sx={{ mb: 2 }}>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
										Error Details
									</Typography>
									<CopyToClipboardButton value={instanceData.errorMessage} />
								</Stack>
								<Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
										{instanceData.errorMessage}
									</Typography>
								</Alert>
							</Box>
						</>
					)}

					<Divider sx={{ my: 2 }} />

					{hasInput && (
						<Box sx={{ mb: 2 }}>
							<JsonDataDisplay
								title="Input"
								data={instanceData.input}
								asAccordion
								defaultExpanded={false}
								settingsKey="flowInstanceDetailsPanel.input"
								accordionExpandedStorageKey={FLOW_INSTANCE_DETAILS_INPUT_EXPANDED_STORAGE_KEY}
							/>
						</Box>
					)}

					{hasOutput && (
						<Box sx={{ mb: 2 }}>
							<JsonDataDisplay
								title="Output"
								data={instanceData.output}
								asAccordion
								defaultExpanded={false}
								settingsKey="flowInstanceDetailsPanel.output"
								accordionExpandedStorageKey={FLOW_INSTANCE_DETAILS_OUTPUT_EXPANDED_STORAGE_KEY}
							/>
						</Box>
					)}

					<Divider sx={{ my: 2 }} />

					{isEditingActivity ? (
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
								Current Activity
							</Typography>
							<Select
								fullWidth
								size="small"
								value={editedActivityId}
								onChange={(e) => setEditedActivityId(e.target.value)}
								disabled={savingActivity}
								sx={{ mb: 1 }}
								MenuProps={{
									sx: { zIndex: 1400 },
								}}
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{nodes
									.filter((node) => {
										const data = node.data as any;
										return data?.Id && data?.Type;
									})
									.map((node) => {
										const data = node.data as any;
										return (
											<MenuItem key={data.Id} value={data.Id}>
												<ActivityNameDisplay activityId={data.Id} getActivityType={getActivityType} getActivityData={getActivityData} />
											</MenuItem>
										);
									})}
							</Select>
							<Stack direction="row" spacing={1} justifyContent="flex-end">
								<Button size="small" variant="outlined" startIcon={<Cancel />} onClick={handleCancelEditActivity} disabled={savingActivity}>
									Cancel
								</Button>
								<Button size="small" variant="contained" startIcon={<Save />} onClick={handleSaveActivity} disabled={savingActivity}>
									{savingActivity ? 'Saving...' : 'Save'}
								</Button>
							</Stack>
						</Box>
					) : (
						<Box sx={{ mb: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
									Current Activity
								</Typography>
								<Tooltip title="Edit activity">
									<IconButton size="small" onClick={handleEditActivity}>
										<Edit fontSize="small" />
									</IconButton>
								</Tooltip>
							</Stack>
							<ActivityNameDisplay
								activityId={instanceData.currentActivityId}
								getActivityType={getActivityType}
								getActivityData={getActivityData}
								variant="body2"
								sx={{ fontWeight: 500, mb: 1, color: instanceData.currentActivityId ? 'text.primary' : 'text.secondary' }}
							/>
						</Box>
					)}

					<Divider sx={{ my: 2 }} />
					<JsonDataDisplay
						title="Current Activity Stack"
						data={instanceData.currentActivityStack}
						asAccordion
						defaultExpanded={false}
						settingsKey="flowInstanceDetailsPanel.currentActivityStack"
						accordionExpandedStorageKey={FLOW_INSTANCE_DETAILS_ACTIVITY_STACK_EXPANDED_STORAGE_KEY}
						editable
						onSave={handleSaveActivityStack}
						saving={savingActivityStack}
					/>

					<Divider sx={{ my: 2 }} />

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
							Execution Timeline
						</Typography>
						<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
							Started: {formatTimestampSortable(instanceData.createdDate)}
						</Typography>
						{instanceData.status === FlowInstanceStatus.Completed || instanceData.status === FlowInstanceStatus.Failed ? (
							<>
								<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
									Ended: {formatTimestampSortable(instanceData.updatedDate)}
								</Typography>
								<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', mt: 1 }}>
									Duration: {formatDuration(dayjs(instanceData.updatedDate).diff(dayjs(instanceData.createdDate), 'millisecond'))}
								</Typography>
							</>
						) : (
							<Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem', mt: 1 }}>
								Running for: {formatDuration(dayjs().diff(dayjs(instanceData.createdDate), 'millisecond'))}
							</Typography>
						)}
					</Box>

					{runsData && runsData.length > 0 && (
						<>
							<Divider sx={{ my: 2 }} />
							<FlowInstanceExecutionRuns
								runs={runsData}
								selectedStepId={selectedStepId}
								onStepClick={handleStepClick}
								getActivityType={getActivityType}
								getActivityData={getActivityData}
								onDeleteRun={handleDeleteRunClick}
							/>
						</>
					)}
				</>
			)}
			<ConfirmDialog
				open={deleteConfirmDialog}
				title="Delete Flow Instance"
				message="Are you sure you want to delete this flow instance? This action cannot be undone and will remove all associated run history."
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
				sx={{ zIndex: 1400 }}
			/>

			<ConfirmDialog
				open={!!deleteRunConfirmDialog}
				title="Delete Flow Run"
				message="Are you sure you want to delete this run? This action cannot be undone and will remove all execution steps for this run."
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDeleteRun}
				onCancel={handleCancelDeleteRun}
				sx={{ zIndex: 1400 }}
			/>

			{instanceData && (
				<RunFlowModal
					open={resumeModalOpen}
					onClose={() => setResumeModalOpen(false)}
					flowId={instanceId}
					flowName={instanceId}
					mode="resume"
					definitionId={instanceData.definitionId}
					environmentId={instanceData.environmentId}
					onSuccess={handleResumeSuccess}
				/>
			)}
		</SidePanelDetail>
	);
}
